'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoData: {
    clientName: string;
    videoUrl: string;
    transcript?: string;
  } | null;
}

const VideoModal = ({ isOpen, onClose, videoData }: VideoModalProps) => {
  const { t } = useLanguage();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !videoData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-card rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading font-semibold text-lg text-foreground">
            {videoData.clientName} - {t('modal.successStory')}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            aria-label={t('modal.closeModal')}
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="relative aspect-video bg-black">
          <iframe
            src={videoData.videoUrl}
            title={`${videoData.clientName} testimonial video`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {videoData.transcript && (
          <div className="p-4 max-h-48 overflow-y-auto bg-muted/30">
            <h4 className="font-heading font-semibold text-sm text-foreground mb-2">
              {t('modal.transcript')}
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {videoData.transcript}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoModal;